const ENGLISH_LABELS = {
  basic: "Basic",
  intermediate: "Intermediate",
  advanced: "Advanced",
  fluent: "Fluent or bilingual",
};

const EXPERIENCE_LABELS = {
  new: "No formal experience",
  under: "Less than 1 year",
  some: "1 to 3 years",
  many: "More than 3 years",
};

const DAY_LABELS = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const WINDOW_LABELS = {
  am: "08:00–12:00",
  pm: "12:00–16:00",
  eve: "16:00–20:00",
  othertime: "Other time",
};

const CHANNEL_LABELS = {
  email: "Email",
  phone: "Phone",
  whatsapp: "WhatsApp",
  text: "Text",
};

const SUMMARY_KEYS = [
  ["name", /^name\s*:/i],
  ["applicantCity", /^city\s*:/i],
  ["email", /^email\s*:/i],
  ["roleType", /^role of interest\s*:/i],
  ["preferredChannel", /^preferred contact\s*:/i],
  ["phone", /^phone\/whatsapp\s*:/i],
  ["englishLevel", /^spoken english\s*:/i],
  ["relevantExperience", /^relevant experience\s*:/i],
  ["interviewDays", /^interview days.*:/i],
  ["interviewWindows", /^interview windows.*:/i],
  ["availabilityNotes", /^availability notes\s*:/i],
  ["resumeFileName", /^r[ée]sum[ée] file\s*:/i],
];

function asText(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "—") return "";
  return text;
}

function lookupLabel(value, labels) {
  const text = asText(value);
  if (!text) return "";
  const key = text.toLowerCase().replace(/[\s-]+/g, "");
  return labels[text.toLowerCase()] || labels[key] || text;
}

function formatCodedList(value, labels) {
  const text = asText(value);
  if (!text) return "";
  return text
    .split(/[,|]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => lookupLabel(part, labels))
    .join(", ");
}

function insertNewlinesBeforeLabels(text) {
  return String(text).replace(
    /\s+(?=(?:Name|City|Email|Role of interest|Preferred contact|Phone\/WhatsApp|Spoken English|Relevant experience|Interview days|Interview windows|Availability notes|R[ée]sum[ée] file)\s*:)/gi,
    "\n"
  );
}

function parseLabeledLines(text) {
  const fields = {};
  if (!text) return fields;
  let currentKey = null;
  String(text)
    .split(/\r?\n/)
    .forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) return;
      const matched = SUMMARY_KEYS.find(([, pattern]) => pattern.test(line));
      if (matched) {
        currentKey = matched[0];
        fields[currentKey] = line.replace(matched[1], "").trim();
        return;
      }
      if (currentKey && !/^[A-Za-z].{0,40}:/.test(line)) {
        fields[currentKey] = `${fields[currentKey]} ${line}`.trim();
      }
    });
  return fields;
}

export function parseApplicationSummary(text) {
  const fromLines = parseLabeledLines(text);
  if (Object.keys(fromLines).length > 1) return fromLines;
  return parseLabeledLines(insertNewlinesBeforeLabels(text));
}

function pick(...values) {
  for (const value of values) {
    const text = asText(value);
    if (text) return text;
  }
  return "";
}

export function applicationFieldsFromOpportunity(opportunity = {}) {
  const source = opportunity || {};
  const parsed = parseApplicationSummary(source.summary);
  return {
    name: pick(source.contactName, parsed.name),
    email: pick(source.contactEmail, parsed.email),
    phone: pick(source.contactPhone, parsed.phone),
    applicantCity: pick(source.applicantCity, parsed.applicantCity),
    preferredChannel: lookupLabel(
      pick(source.preferredChannel, source.preferredContactMethod, parsed.preferredChannel),
      CHANNEL_LABELS
    ),
    roleType: pick(source.roleType, source.interestedIn, parsed.roleType),
    englishLevel: lookupLabel(pick(source.englishLevel, parsed.englishLevel), ENGLISH_LABELS),
    relevantExperience: lookupLabel(
      pick(source.relevantExperience, parsed.relevantExperience),
      EXPERIENCE_LABELS
    ),
    interviewDays: formatCodedList(pick(source.interviewDays, parsed.interviewDays), DAY_LABELS),
    interviewWindows: formatCodedList(
      pick(source.interviewWindows, parsed.interviewWindows),
      WINDOW_LABELS
    ),
    availabilityNotes: pick(source.availabilityNotes, parsed.availabilityNotes),
    resumeFileName: pick(source.resumeFileName, parsed.resumeFileName),
    capturePage: pick(source.capturePage, parsed.capturePage),
  };
}

export function hasParsedApplicationAnswers(fields = {}) {
  return Boolean(
    fields.roleType ||
      fields.englishLevel ||
      fields.relevantExperience ||
      fields.interviewDays ||
      fields.interviewWindows ||
      fields.availabilityNotes ||
      fields.resumeFileName
  );
}
